'use client';

import toast from 'react-hot-toast';
import { useEffectOnce } from 'react-use';
import { useModal } from '@ebay/nice-modal-react';
import copy from 'clipboard-copy';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ClockIcon, SquareStackIcon } from '@zep/icons';
import { IconButton, Spacing } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { usePlanDetail } from '../../../../services';
import { BankCodes, SubscribePlan } from '../../../../types';
import { PhoneNumberInputDialog } from '../../subscription';
import { OnetimePeriod, PlanTemplateType } from '../types';
import { OnetimeSpaceInfo, SuccessActionButtons } from './shared';
export const PlanPaymentPendingDeposit = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    query
  } = useRouter();
  const spacePlanHashId = (query.spacePlanHashId || '').toString();
  const {
    data: planDetailData
  } = usePlanDetail(spacePlanHashId);
  const spaceHashId = planDetailData?.space?.hashId || '';
  const entryMapHashId = planDetailData?.space?.entryMapHashId || '';
  const title = t('subscription:payment.waiting.title', '입금 대기중');
  const maxCcu = String(planDetailData?.spacePlan?.maxCcu) || '';
  const plan = (planDetailData?.spacePlan?.planType || '').toLowerCase() as SubscribePlan;
  const thumbnailUrl = planDetailData?.space?.thumbnailUrl || '';
  const spaceName = planDetailData?.space?.name || '';
  const onetimePeriod = planDetailData?.spacePlan?.onetimePeriod || '';
  const orderId = planDetailData?.paymentInfo?.orderId || '';
  const dueDate = planDetailData?.paymentInfo?.virtualAccountInfo?.dueDate || '';
  const bankCode = planDetailData?.paymentInfo?.virtualAccountInfo?.bankCode;
  const accountNumber = planDetailData?.paymentInfo?.virtualAccountInfo?.accountNumber;
  const price = planDetailData?.paymentInfo?.virtualAccountInfo?.finalPrice || 0;
  const phoneNumberInputModal = useModal(PhoneNumberInputDialog);
  useEffectOnce(() => {
    phoneNumberInputModal.show({
      spaceHashId: String(spaceHashId),
      planTemplateType: PlanTemplateType.PRODUCT_ONETIME,
      spaceName,
      spaceUrl: `zep.us/play/${planDetailData?.space?.entryMapHashId || ''}`,
      planType: plan.toUpperCase()
    });
  });
  return <>
      <div className="flex h-[calc(100vh-64px)] items-center px-8">
        <div className="user-select-none mx-auto flex min-h-[470px] w-full max-w-[716px] flex-col rounded-[10px] bg-white shadow-[0_2px_10px_0_rgba(42,39,65,0.10)]">
          <div className="flex-1 px-[40px] py-[30px]">
            <h1 className="text-h5 font-[700] text-gray-900">{title}</h1>
            <div className="flex h-[calc(100%-30px)] flex-col pt-[24px] md:flex-row">
              <section className="flex flex-col gap-3 pr-[20px]">
                <OnetimeSpaceInfo maxCcu={maxCcu} plan={plan} thumbnailUrl={thumbnailUrl} spaceName={spaceName} onetimePeriod={onetimePeriod as OnetimePeriod} data-sentry-element="OnetimeSpaceInfo" data-sentry-source-file="PlanPaymentPendingDeposit.tsx" />
              </section>
              <section className="w-full min-w-[300px] pl-[20px]">
                <div className="text-body-2-m font-[500] text-gray-500">
                  {t('subscription:payment.complete.orderNumber')} {orderId}
                </div>
                <Spacing size={20} data-sentry-element="Spacing" data-sentry-source-file="PlanPaymentPendingDeposit.tsx" />
                <div className="flex flex-col">
                  <PendingDepositNoticeText spaceName={spaceName} plan={plan} maxCcu={+maxCcu} dueDate={dueDate} data-sentry-element="PendingDepositNoticeText" data-sentry-source-file="PlanPaymentPendingDeposit.tsx" />
                  <Spacing size={20} data-sentry-element="Spacing" data-sentry-source-file="PlanPaymentPendingDeposit.tsx" />
                </div>
                <div className="flex w-full flex-col">
                  <div className="flex w-full flex-col gap-[10px] rounded-t-[10px] border border-gray-200 bg-gray-50 px-[20px] pb-[12px] pt-[14px]">
                    <div className="flex w-full justify-between">
                      <span className="text-body-2-m font-[500] text-[#27262EE5]">
                        {t('subscription:payment.waiting.paymentMethod', '결제 수단')}
                      </span>
                      <span className="text-body-2-sb font-[600] text-[#27262ECC]">
                        {t('subscription:SpaceDetail.menuManage.virtualCardVirtualTransfer', '가상 계좌(무통장 입금)')}
                      </span>
                    </div>
                    <div className="flex w-full justify-between">
                      <span className="text-body-2-m font-[500] text-[#27262EE5]">
                        {t('subscription:payment.waiting.bank', '입금 은행')}
                      </span>
                      <span className="text-body-2-sb font-[600] text-[#27262ECC]">
                        {t('subscription:SpaceDetail.menuManage.virtualCardZepInfo', {
                        defaultValues: '{{bankName}} (예금주 : 주식회사 젭)',
                        bankName: BankCodes[bankCode as keyof typeof BankCodes]
                      })}
                      </span>
                    </div>
                    <div className="flex w-full justify-between">
                      <span className="text-body-2-m font-[500] text-[#27262EE5]">
                        {t('subscription:payment.waiting.bankAccount', '입금 계좌번호')}
                      </span>
                      <div className="flex items-center">
                        <IconButton size="xxxs" variant="transparent" intent="sub" onClick={async () => {
                        if (accountNumber != null) {
                          // Note: navigator.clipboard 를 우선 사용하는 방식으로 구현된 clipboard-copy가 hack으로만 구현된 copy-to-clipboard보다 낫습니다.
                          // TODO: copy-to-clipboard를 clipboard-copy로 변경해야 합니다.
                          await copy(accountNumber);
                          toast.success(t('subscription:SpaceDetail.menuManage.virtualAccountNumberCopied', '계좌 번호가 복사되었습니다.'), {
                            position: 'top-center',
                            id: 'copy-account-number'
                          });
                        }
                      }} data-sentry-element="IconButton" data-sentry-source-file="PlanPaymentPendingDeposit.tsx">
                          <SquareStackIcon width={10} height={10} fill={'#27262EA3'} data-sentry-element="SquareStackIcon" data-sentry-source-file="PlanPaymentPendingDeposit.tsx" />
                        </IconButton>
                        <span className="text-body-2-sb font-[600] text-[#27262ECC]">
                          {accountNumber}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-b-[10px] border-x border-b border-gray-200 bg-gray-50 px-[20px] pb-[14px] pt-[12px]">
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body-1-sb font-[600] text-gray-900">
                      {t('subscription:SpaceDetail.menuManage.virtualCardPrice', '결제 금액')}
                    </span>
                    <span className="text-h5 font-[700] text-gray-900">
                      {`₩ ${price.toLocaleString()}`}
                    </span>
                  </div>
                </div>
                <Spacing size={10} data-sentry-element="Spacing" data-sentry-source-file="PlanPaymentPendingDeposit.tsx" />
                <div className="text-caption-1 font-[600] text-gray-500">
                  {t('subscription:SpaceDetail.menuManage.virtualCardWarning1', '입금 후 입금 확인까지 2분 이상의 시간이 소요될 수 있습니다.')}
                </div>
              </section>
            </div>
          </div>
          <div className="flex w-full justify-end px-[40px] pb-[30px] pt-[20px]">
            <SuccessActionButtons spaceHashId={spaceHashId} entryMapHashId={entryMapHashId} data-sentry-element="SuccessActionButtons" data-sentry-source-file="PlanPaymentPendingDeposit.tsx" />
          </div>
        </div>
      </div>
      <style global jsx>{`
        body {
          background-color: #f7fafc;
        }
      `}</style>
    </>;
};
const PendingDepositNoticeText = ({
  spaceName,
  plan,
  maxCcu,
  dueDate
}: {
  spaceName: string;
  plan: string;
  maxCcu: number;
  dueDate: string;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  return <div className="flex w-full flex-col" data-sentry-component="PendingDepositNoticeText" data-sentry-source-file="PlanPaymentPendingDeposit.tsx">
      <div className="flex w-full items-center gap-[3px]">
        <span>
          <ClockIcon width={16} height={16} fill={Colors.RedDefault} data-sentry-element="ClockIcon" data-sentry-source-file="PlanPaymentPendingDeposit.tsx" />
        </span>
        <span className="text-[15px] font-[600] text-red">
          {t('subscription:payment.waiting.subTitle', {
          defaultValues: '{{date}} 까지 미입금 시 자동 취소됩니다',
          date: format(new Date(dueDate), 'yyyy. MM. dd hh:mma')
        })}
        </span>
      </div>
      <Spacing size={8} data-sentry-element="Spacing" data-sentry-source-file="PlanPaymentPendingDeposit.tsx" />
      <div className="text-caption-1 font-[400] text-gray-600">
        {t('subscription:payment.waiting.description', {
        defaultValues: '입금이 확인되면, {{spaceName}} 스페이스가 이용 기간 동안 {{plan}} {{ccu}} 플랜으로 업그레이드 됩니다. 입금 계좌 번호는 [결제 관리]에서 확인할 수 있습니다.',
        spaceName,
        plan: plan.toUpperCase(),
        ccu: maxCcu
      })}
      </div>
    </div>;
};